import _ from 'lodash';

import {
  AppEventName,
  EmailSettingInput,
  EmailSettingsWidgetDocument,
  useEmailSettingsWidgetQuery,
  useSaveStoreEmailSettingsMutation
} from 'generated/graphql';
import {
  useCallback,
  useMemo
} from 'react';

import * as types from './types';



export interface IData {
  appEventName2DisplayName: Record<AppEventName, string>;
  formState: types.IFormState;
}

export function useQuery(storeId: string) {
  const tuple = useEmailSettingsWidgetQuery({
    variables: {
      storeId
    }
  });

  return useMemo(
    () => {
      const appEventName2AppEvent = _.keyBy(tuple.data?.appEvents, appEvent => appEvent.name);

      const emailSettings = tuple.data?.store?.emailSettings;
      const appEventName2TemplateId = _.mapValues(appEventName2AppEvent, ev => {
        const setting = _.find(emailSettings, setting => setting.appEvent.name === ev.name);
        return {
          isActive: setting?.isActive ?? true,
          templateId: setting?.templateId ?? ''
        };
      });

      const data: IData = {
        appEventName2DisplayName: _.mapValues(
          appEventName2AppEvent,
          val => val.displayName
        ) as Record<AppEventName, string>,

        formState: {
          fromEmail: tuple.data?.store?.sendGridFromEmail ?? '',
          fromName: tuple.data?.store?.sendGridFromName ?? '',
          emailSettings: _.map(
            appEventName2TemplateId,
            (setting, appEventName): types.ITemplateSetting => ({
              appEvent: appEventName as AppEventName,
              templateId: setting.templateId,
              isActive: setting.isActive
            }))
        }
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useSaveStoreEmailSettings(storeId: string) {
  const [fnMutate, tuple] = useSaveStoreEmailSettingsMutation({
    refetchQueries: [
      {
        query: EmailSettingsWidgetDocument,
        variables: {storeId}
      }
    ]
  });

  const fnWrappedMutate = useCallback(
    (formState: types.IFormState) => {
      fnMutate({
        variables: {
          input: {
            storeId,
            fromName: formState.fromName,
            fromEmail: formState.fromEmail,
            settings: _.map(formState.emailSettings, (setting): EmailSettingInput => ({
              templateId: setting.templateId,
              appEventName: setting.appEvent,
              isActive: setting.isActive && !_.isEmpty(setting.templateId)
            }))
          }
        }
      });
    },
    [
      fnMutate,
      storeId
    ]
  );

  return useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
